import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { FaHistory, FaFire } from 'react-icons/fa';
import { loadRecentSearches, describeRecentSearch, clearRecentSearches } from './buscadorRecentSearches';

const HINT_FIELDS = ['nombre_producto', 'vehiculo', 'nombre_marca', 'sku'];

const BuscadorSearchHints = ({
  token,
  filters,
  vehiculos = [],
  categoryOptions = [],
  onApplyRecentSearch,
  onApplyFrequentQuery,
}) => {
  const [popularByField, setPopularByField] = useState({});
  const [frequentCombos, setFrequentCombos] = useState([]);
  const [fieldHints, setFieldHints] = useState({});
  const [recentSearches, setRecentSearches] = useState([]);

  const authHeaders = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  useEffect(() => {
    setRecentSearches(loadRecentSearches());
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadPopular = async () => {
      try {
        const response = await axios.get('/api/productos/busquedas-hints', {
          headers: authHeaders,
          params: { scope: 'global' },
        });
        if (!cancelled && response.data) {
          if (response.data.byField) setPopularByField(response.data.byField);
          setFrequentCombos(response.data.frequentCombos || []);
        }
      } catch (err) {
        console.warn('No se pudieron cargar hints de búsqueda:', err.message);
      }
    };
    loadPopular();
    return () => { cancelled = true; };
  }, [authHeaders]);

  const fetchFieldHints = useCallback(async (field, q) => {
    if (!q || q.length < 2) return;
    try {
      const response = await axios.get('/api/productos/busquedas-hints', {
        headers: authHeaders,
        params: { field, q, scope: 'global' },
      });
      const suggestions = response.data?.suggestions || [];
      setFieldHints((prev) => ({ ...prev, [field]: suggestions }));
    } catch {
      // silent
    }
  }, [authHeaders]);

  useEffect(() => {
    const timeouts = {};
    HINT_FIELDS.forEach((field) => {
      const value = filters[field];
      if (!value || value.length < 2) return;
      timeouts[field] = setTimeout(() => fetchFieldHints(field, value), 300);
    });
    return () => Object.values(timeouts).forEach(clearTimeout);
  }, [filters.nombre_producto, filters.vehiculo, filters.nombre_marca, filters.sku, fetchFieldHints]);

  const vehiculoOptions = useMemo(() => {
    const base = vehiculos.map((v) => v.vehiculo_completo);
    const hinted = (fieldHints.vehiculo?.length ? fieldHints.vehiculo : popularByField.vehiculo || [])
      .map((item) => item.value);
    return [...new Set([...base, ...hinted])];
  }, [vehiculos, fieldHints.vehiculo, popularByField.vehiculo]);

  const handleRecentClick = (entry) => {
    onApplyRecentSearch(entry);
    setRecentSearches(loadRecentSearches());
  };

  const handleClearRecent = () => {
    clearRecentSearches();
    setRecentSearches([]);
  };

  const handleFrequentClick = (combo) => {
    if (onApplyFrequentQuery && combo.filters?.length) {
      onApplyFrequentQuery(combo.filters);
    }
  };

  const datalistFor = (field) => {
    if (field === 'vehiculo') return vehiculoOptions.map((v) => ({ value: v }));
    const pool = fieldHints[field]?.length ? fieldHints[field] : (popularByField[field] || []);
    return pool.slice(0, 20);
  };

  return (
    <>
      {(frequentCombos.length > 0 || recentSearches.length > 0) && (
        <div className="space-y-3 mb-2">
          {frequentCombos.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-white/80 shrink-0">
                <FaFire className="text-yellow-400" /> Frecuentes
              </span>
              {frequentCombos.slice(0, 6).map((combo, idx) => (
                <button
                  key={`${combo.display}-${idx}`}
                  type="button"
                  onClick={() => handleFrequentClick(combo)}
                  className="px-2.5 py-1 rounded-full text-xs font-medium bg-white/15 text-white border border-white/25 hover:bg-yellow-500 hover:text-black hover:border-yellow-500 transition-colors max-w-[220px] truncate"
                  title={`${combo.count} búsquedas`}
                >
                  {combo.display}
                </button>
              ))}
            </div>
          )}
          {recentSearches.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-white/80 shrink-0">
                <FaHistory /> Recientes
              </span>
              {recentSearches.slice(0, 5).map((entry, idx) => (
                <button
                  key={`${entry.ts}-${idx}`}
                  type="button"
                  onClick={() => handleRecentClick(entry)}
                  className="px-2.5 py-1 rounded-full text-xs font-medium bg-white/10 text-white/90 border border-white/20 hover:bg-white/25 transition-colors max-w-[220px] truncate"
                  title={describeRecentSearch(entry, { categoryOptions })}
                >
                  {describeRecentSearch(entry, { categoryOptions })}
                </button>
              ))}
              <button
                type="button"
                onClick={handleClearRecent}
                className="text-[10px] font-semibold text-white/60 hover:text-white underline underline-offset-2"
              >
                Limpiar recientes
              </button>
            </div>
          )}
        </div>
      )}
      {HINT_FIELDS.map((field) => (
        <datalist key={field} id={`buscador-hints-${field}`}>
          {datalistFor(field).map((item) => (
            <option key={item.value} value={item.value} />
          ))}
        </datalist>
      ))}
    </>
  );
};

export default BuscadorSearchHints;
